/**
 * Single HTTP client for the stamp admin-app. Global fetch only; JSON in,
 * JSON out; server `{error}` bodies surfaced as readable messages; 401s get
 * a "run ghosty login" hint.
 */
import { cliVersion } from './version.js';
import { exitCliOutdated } from './output.js';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * A pre-derived brand theme some stamps return on /api/config (the runtime
 * theming work). When present the CLI passes it straight through; otherwise it
 * derives an equivalent theme from the raw brand inputs below via
 * @ghosty/branding. Mirrors @ghosty/scaffolder's ScaffoldTheme shape.
 */
export interface PlatformTheme {
  tokens: Record<string, string> & { mode: 'light' | 'dark' };
  fontDisplayStack: string;
  fontMonoStack: string;
  fontGoogleUrl: string;
  logoUrl?: string;
}

export interface PlatformConfig {
  productName: string;
  logoUrl?: string | null;
  colors?: { primary: string; accent?: string; secondary?: string } | null;
  tenantId: string;
  adminUrl: string;
  marketplaceUrl: string;
  // Added 2026-06 for CLI scaffolding; older stamps may not return these.
  baseDomain?: string;
  gcpRegion?: string;
  gcpProjectId?: string;
  // Brand inputs for baking the full app theme. Older stamps omit these; the
  // CLI then falls back to colours.primary + DEFAULT_BRAND.
  mode?: 'light' | 'dark';
  font?: string;
  /** A fully pre-derived theme; when present the CLI uses it verbatim. */
  theme?: PlatformTheme | null;
}

export class ApiClient {
  constructor(
    public readonly adminUrl: string,
    private readonly token?: string,
  ) {}

  async request<T = unknown>(
    route: string,
    options: { method?: string; body?: unknown } = {},
  ): Promise<T> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-CLI-Version': cliVersion(),
    };
    if (this.token) headers.Authorization = `Bearer ${this.token}`;
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';

    let res: Response;
    try {
      res = await fetch(new URL(route, `${this.adminUrl}/`), {
        method: options.method ?? 'GET',
        headers,
        body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      });
    } catch (err) {
      throw new ApiError(
        `Could not reach ${this.adminUrl}: ${(err as Error).message}. Check your network and the admin URL.`,
        0,
      );
    }

    const text = await res.text();
    let parsed: unknown = null;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      /* non-JSON body, handled below */
    }

    if (!res.ok) {
      const serverError =
        parsed && typeof parsed === 'object' && typeof (parsed as { error?: unknown }).error === 'string'
          ? (parsed as { error: string }).error
          : text.slice(0, 300) || res.statusText;
      if (
        res.status === 426 &&
        parsed &&
        typeof parsed === 'object' &&
        (parsed as { code?: unknown }).code === 'cli_outdated'
      ) {
        // Hard stop here at the choke point: commands catch ApiError with
        // generic handling, but this case needs its own code + exit status.
        exitCliOutdated(serverError);
      }
      let message = `${res.status}: ${serverError}`;
      const issues = (parsed as { issues?: unknown } | null)?.issues;
      if (Array.isArray(issues) && issues.length > 0) {
        message += ` (${issues.join('; ')})`;
      }
      if (res.status === 401) {
        message += ' — run `ghosty login` to sign in.';
      }
      throw new ApiError(message, res.status, parsed);
    }
    return parsed as T;
  }

  get<T = unknown>(route: string): Promise<T> {
    return this.request<T>(route);
  }

  post<T = unknown>(route: string, body?: unknown): Promise<T> {
    return this.request<T>(route, { method: 'POST', body });
  }

  /** Public, unauthenticated stamp config (branding + scaffolding facts). */
  fetchConfig(): Promise<PlatformConfig> {
    return this.get<PlatformConfig>('/api/config');
  }
}

/**
 * Derive the stamp base domain when /api/config predates the baseDomain
 * field: apps live at {slug}.{baseDomain} and admin at admin.{baseDomain},
 * so strip the leading "admin." from the admin host.
 */
export function deriveBaseDomain(adminUrl: string, platformConfig?: PlatformConfig): string {
  if (platformConfig?.baseDomain) return platformConfig.baseDomain;
  const host = new URL(adminUrl).host;
  return host.startsWith('admin.') ? host.slice('admin.'.length) : host;
}
